import type { CreateSearchRequest } from '@legalhermes/shared/api-contracts';
import type { Search as SearchEntity } from '@legalhermes/shared/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { cn } from '../lib/utils';
import { api } from '../services/api';
import { SearchModal } from './SearchModal';

function Searches() {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const accountDisabled = organization?.disabled === true;
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSearch, setEditingSearch] = useState<SearchEntity | null>(null);

  const { data: searches = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.searches,
    queryFn: api.getSearches,
  });

  const invalidateSearches = () => queryClient.invalidateQueries({ queryKey: queryKeys.searches });

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const { mutate: saveSearch, isPending: isSaving } = useMutation({
    mutationFn: ({ id, data }: { id: string | null; data: CreateSearchRequest }) =>
      id ? api.updateSearch(id, data) : api.addSearch(data),
    onSuccess: () => {
      invalidateSearches();
      showMessage('Búsqueda guardada correctamente');
    },
    onError: (err: Error) => showMessage(err?.message || 'Error al guardar búsqueda'),
  });

  const { mutate: deleteSearch } = useMutation({
    mutationFn: api.deleteSearch,
    onSuccess: () => {
      invalidateSearches();
      showMessage('Búsqueda eliminada');
    },
    onError: () => showMessage('Error al eliminar búsqueda'),
  });

  const handleSaveSearch = (searchData: CreateSearchRequest) => {
    saveSearch({ id: editingSearch?.id ?? null, data: searchData });
  };

  const handleDeleteSearch = (searchId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar esta búsqueda?')) return;
    deleteSearch(searchId);
  };

  return (
    <div>
      {/* Page header */}
      <div className="mb-8 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-[1.35rem] font-semibold text-ink tracking-tight flex items-center gap-2.5">
            <Search size={20} className="text-accent flex-shrink-0" />
            Búsquedas
          </h3>
          <p className="text-ink-tertiary text-[0.875rem] mt-2 leading-snug">
            Personas o procesos que el monitor revisa automáticamente
          </p>
        </div>
        <button
          className="px-3 sm:px-4 py-2 bg-accent text-ink-inverse border-none rounded-sm text-[0.8125rem] font-medium font-sans inline-flex items-center gap-2 cursor-pointer hover:bg-accent-hover transition-[background-color] duration-150 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent"
          onClick={() => {
            setEditingSearch(null);
            setIsModalOpen(true);
          }}
          disabled={accountDisabled}
          title={accountDisabled ? 'Cuenta desactivada' : undefined}
          aria-label="Agregar búsqueda"
        >
          <Plus size={14} />
          <span className="hidden sm:inline">Agregar</span>
        </button>
      </div>

      {/* Message */}
      {message && (
        <div
          className={cn(
            'mb-4 px-4 py-3 rounded-sm text-[0.875rem] font-medium',
            message.includes('Error')
              ? 'bg-destructive-subtle text-destructive'
              : 'bg-success-subtle text-success'
          )}
        >
          {message}
        </div>
      )}

      {/* List */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-ink-muted text-[0.875rem]">Cargando...</div>
        ) : searches.length === 0 ? (
          <div className="py-16 px-8 text-center text-ink-muted text-[0.875rem]">
            No hay búsquedas configuradas. Agrega una para comenzar.
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {searches.map((search) => (
              <li
                key={search.id}
                className="px-4 sm:px-6 py-4 flex items-start gap-3 sm:gap-4 hover:bg-surface-raised transition-colors duration-150"
              >
                <div
                  className={cn(
                    'mt-2 w-2 h-2 rounded-full flex-shrink-0',
                    search.enabled ? 'bg-success' : 'bg-ink-muted'
                  )}
                  title={search.enabled ? 'Activa' : 'Inactiva'}
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className={cn(
                        'px-2.5 py-0.5 rounded-full text-[0.7rem] font-semibold tracking-wide',
                        search.type === 'name'
                          ? 'bg-accent-subtle text-accent'
                          : 'bg-success-subtle text-success'
                      )}
                    >
                      {search.type === 'name' ? 'Nombre' : 'Proceso'}
                    </span>
                    {!search.enabled && (
                      <span className="text-[0.7rem] font-medium text-ink-muted uppercase tracking-wide">
                        Inactiva
                      </span>
                    )}
                  </div>

                  <div
                    className={cn(
                      'text-[0.9rem] text-ink font-medium leading-snug',
                      search.type === 'process'
                        ? 'font-mono text-[0.8125rem] break-all'
                        : 'break-words'
                    )}
                  >
                    {search.identifier}
                  </div>

                  <div className="text-[0.8125rem] text-ink-tertiary mt-1 truncate">
                    {search.email ? (
                      search.email
                    ) : (
                      <span className="italic">Email de la organización</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-0.5 flex-shrink-0 -mr-1.5">
                  <button
                    className="bg-none border-none cursor-pointer p-2.5 rounded-sm text-ink-tertiary hover:text-ink hover:bg-nav-hover transition-[color,background-color] duration-150 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => {
                      setEditingSearch(search);
                      setIsModalOpen(true);
                    }}
                    disabled={accountDisabled}
                    aria-label="Editar búsqueda"
                    title={accountDisabled ? 'Cuenta desactivada' : 'Editar'}
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    className="bg-none border-none cursor-pointer p-2.5 rounded-sm text-ink-tertiary hover:text-destructive hover:bg-destructive-subtle transition-[color,background-color] duration-150 inline-flex items-center disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={() => handleDeleteSearch(search.id)}
                    disabled={accountDisabled}
                    aria-label="Eliminar búsqueda"
                    title={accountDisabled ? 'Cuenta desactivada' : 'Eliminar'}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <SearchModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSearch(null);
        }}
        onSave={handleSaveSearch}
        editingSearch={editingSearch}
        isSaving={isSaving}
      />
    </div>
  );
}

export default Searches;
